import { useCallback, useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { formatMessage } from 'src/i18n/intl';
import { Tabs, Tab } from '@cw-ui/navigation-components';
import Box from '@mui/material/Box';
import Locale from 'src/i18n';
import React, { useState, useEffect, useTransition } from 'react';
import { PERMISSIONS } from 'src/constants';
import { checkIfUserHaveGivenPermissions } from 'src/util/Util';
import { withRouter } from 'src/components/withRouter';
import css from 'src/styles/App.scss';

const getInitialSelectedId = pathname => {
    switch (pathname) {
        case '/':
        case '/signin-oidc':
        case '/pendingInvites':
        case '/invites':
            return '/users';
        case '/registrationToken':
            return '/products';
        case '/application-roles':
        case '/application-roles/permissions':
        case '/application-roles/content-assignment':
        case '/application-roles/users':
            return '/application-roles';
        case '/guest-users':
            return '/guest-users';
        default:
            return pathname;
    }
};

const NavigationTabs = props => {
    const {
        location,
        onDidMount,
        onWillUnmount,
        hideTabs,
        navigate,
        originPartnerId,
        showApplicationRoleManagementTab,
        hasNewIdentityRolesFeature,
        hasNewUsersFeature,
        hasNewProductsFeature,
        shouldShowSubscribers,
    } = props;

    const [, startTransition] = useTransition();

    const shouldRenderUsersTab = checkIfUserHaveGivenPermissions(
        [
            PERMISSIONS.USERS_ENABLE,
            PERMISSIONS.USERS_EMAIL_RESEND,
            PERMISSIONS.USERS_VIEW,
            PERMISSIONS.USERS_PASSWORD_RESET,
        ],
        true
    );

    const shouldRenderIdentityRolesTab = checkIfUserHaveGivenPermissions([PERMISSIONS.ROLES_VIEW]);

    // Build tab configuration array
    const tabConfig = useMemo(() => {
        const config = [];

        if (shouldRenderUsersTab) {
            config.push({ id: '/users', label: formatMessage(Locale.currentUsers) });
        }

        if (shouldRenderUsersTab && originPartnerId) {
            config.push({ id: '/guest-users', label: formatMessage(Locale.guestUsers) });
        }

        config.push({ id: '/authentication', label: formatMessage(Locale.authentication) });

        if (shouldRenderIdentityRolesTab) {
            config.push({ id: '/roles', label: formatMessage(Locale.identityRoles) });
        }

        config.push({ id: '/products', label: formatMessage(Locale.products) });

        if (showApplicationRoleManagementTab) {
            config.push({ id: '/application-roles', label: formatMessage(Locale.applicationRoles) });
        }

        if (hasNewIdentityRolesFeature) {
            config.push({ id: '/identityRoles', label: formatMessage(Locale.identityRolesMUI) });
        }

        if (hasNewUsersFeature) {
            config.push({ id: '/usersNew', label: formatMessage(Locale.usersNew) });
        }

        if (hasNewProductsFeature) {
            config.push({ id: '/products/v2', label: formatMessage(Locale.productsTabV2) });
        }

        if (shouldShowSubscribers) {
            config.push({ id: '/subscribers', label: formatMessage(Locale.subscribersTab) });
        }

        return config;
    }, [
        shouldRenderUsersTab,
        shouldRenderIdentityRolesTab,
        originPartnerId,
        showApplicationRoleManagementTab,
        hasNewIdentityRolesFeature,
        hasNewUsersFeature,
        hasNewProductsFeature,
        shouldShowSubscribers,
    ]);

    // Map route to tab index
    const getIndexFromRoute = useCallback(
        route => {
            const normalizedRoute = getInitialSelectedId(route);
            const index = tabConfig.findIndex(tab => tab.id === normalizedRoute);
            return Math.max(0, index);
        },
        [tabConfig]
    );

    // Get current tab index from location
    const currentTabIndex = useMemo(() => {
        return getIndexFromRoute(location.pathname);
    }, [location.pathname, getIndexFromRoute]);

    const [value, setValue] = useState(currentTabIndex);

    useEffect(() => {
        onDidMount();
        return () => {
            onWillUnmount();
        };
    }, [onDidMount, onWillUnmount]);

    // Update tab value when location changes
    useEffect(() => {
        setValue(currentTabIndex);
    }, [currentTabIndex]);

    const handleChange = useCallback(
        (_, newValue) => {
            startTransition(() => {
                props.onNavigate();
                const selectedRoute = tabConfig[newValue]?.id;
                if (selectedRoute) {
                    navigate(selectedRoute);
                }
            });
        },
        [tabConfig, navigate, props]
    );

    if (hideTabs) {
        return null;
    }

    return (
        <Box className={css.tabPanel}>
            <Tabs value={value} onChange={handleChange} option="primary">
                {tabConfig.map(tab => (
                    <Tab key={tab.id} label={tab.label} option="primary" />
                ))}
            </Tabs>
        </Box>
    );
};

export default withRouter(injectIntl(NavigationTabs));
